// Copyright (c) Docugami, Inc. All rights reserved.
import { DOMParser } from '@xmldom/xmldom'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// This module is more or less equivalent to AbstractXmlProducer.cs
//
// In both AbstractGenerator, and the web app, I first transform DGML to a AbstractXmlV2.  The purposes of doing this are:
// - It is easier and clearer to write code to transform AbstractXMLV2 to WordprocessingML, rather that going straight from
//   DGML to WordprocessingML.
// - This makes the two modules more or less equivalent - the processes of transforming are the same for both.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/*eslint-disable */

// Note: the following const have eslint-disable because they form the whole of the Open-Xml-Sdk for JavaScript API.
// Most modules will use only a subset of the API, but disabling portions of the API leads to friction when writing
// code.  Disabling eslint for these variables removes the error indicating that a variable is assigned a value but
// never used.  If we ever re-write these modules in TypeScript, then the public API will change significantly.

const Enumerable = require("../../../../../utils/linq");
const Ltxml = require('../../../../../utils/ltxml.js');
Ltxml.DOMParser = DOMParser

const openXml = require('../../../../../utils/openxml');

const XAttribute = Ltxml.XAttribute;
const XCData = Ltxml.XCData;
const XComment = Ltxml.XComment;
const XContainer = Ltxml.XContainer;
const XDeclaration = Ltxml.XDeclaration;
const XDocument = Ltxml.XDocument;
const XElement = Ltxml.XElement;
const XName = Ltxml.XName;
const XNamespace = Ltxml.XNamespace;
const XNode = Ltxml.XNode;
const XObject = Ltxml.XObject;
const XProcessingInstruction = Ltxml.XProcessingInstruction;
const XText = Ltxml.XText;
const XEntity = Ltxml.XEntity;
const cast = Ltxml.cast;
const castInt = Ltxml.castInt;

const W = openXml.W;
const W14 = openXml.W14;
const W15 = openXml.W15;
const NN = openXml.NoNamespace;
const wNs = openXml.wNs;
/*eslint-enable */

export function produceAbstractXml(chunkXmlToInsert: string): any
{
    const chunkXml: any = XElement.parse(chunkXmlToInsert);
    const abstractXml = produceAbstractXmlTransform(chunkXml);
    const dummy = new XElement("dummy", abstractXml);
    const theArrayOfAbstractXml = dummy.elements().toArray();
    return theArrayOfAbstractXml;
}

function produceAbstractXmlTransform(node: any): any
{
    if (node.nodeType === 'Element')
    {
        const element = node;
        const structureAttribute = element.attribute('structure');

        // if there is only one element, and there are no child elements, then return a <p> element with the text contents
        if (structureAttribute === null && ! element.hasElements)
        {
            if (element.parent === null)
            {
                return new XElement('p', element.value);
            }
            else
            {
                return element.nodes().select((n: any) => produceAbstractXmlTransform(n));
            }
        }

        if (structureAttribute !== null)
        {
            const splitValue = structureAttribute.value.split('|');
            const structure = splitValue[0];

            if (structure === 'ol')
            {
                return new XElement('ol',
                    element.attribute('style'),
                    element.nodes().select((n: any) => produceAbstractXmlTransform(n)));
            }

            if (structure === 'ul')
            {
                return new XElement('ul',
                    element.attribute('style'),
                    element.nodes().select((n: any) => produceAbstractXmlTransform(n)));
            }

            if (structure === 'lim')
            {
                return null;
            }

            if (structure === 'li' || structure === 'p' || structure === 'table' || structure === 'tbody' || structure === 'tr' )
            {
                return new XElement(structure,
                    element.nodes().select((n: any) => produceAbstractXmlTransform(n)));
            }

            if (structure === 'td')
            {
                const newTd = new XElement('td',
                    element.attribute('colspan'),
                    element.attribute('rowspan'),
                    element.nodes().select((n: any) => produceAbstractXmlTransform(n)));
                return newTd;
            }

            if (structure === 'div')
            {
                // if there is a div, and it contains just one element, which is a p, li, or h1, then collapse
                if (element.elements().count() === 1)
                {
                    const theElement = element.elements().first();
                    if (isParagraphElement(theElement))
                    {
                        return element.nodes().select((n: any) => produceAbstractXmlTransform(n));
                    }
                }


                return new XElement('div',
                    element.nodes().select((n: any) => produceAbstractXmlTransform(n)));
            }

        }

        // if there is no structure attribute, and there are child elements, then collapse
        if (structureAttribute === null && element.hasElements)
        {
            return element.nodes().select((n: any) => produceAbstractXmlTransform(n));
        }

        const txElement = new XElement(element.name,
            element.attributes(),
            element.nodes().select((n: any) => produceAbstractXmlTransform(n)));
        return txElement;
    }

    if (node.nodeType === 'Text')
    {
        if (node.value.trim() !== "")
        {
            return node;
        }
        return null;
    }
    return node;
}

function isParagraphElement(element: any): boolean
{
    const structureAttribute = element.attribute('structure');
    if (structureAttribute === null)
    {
        return false;
    }
    const splitValue = structureAttribute.value.split('|');
    const structure = splitValue[0];
    if (structure === 'p' || structure === 'h1' || structure === 'li')
    {
        return true;
    }
    return false;
}

/*
Following is the list of elements that this transform produces:

<p> paragraph element
<div>
    {text}
    <p>
    <ol>
    <ul>
        A div can contain p, ol, ul, and table elements.

<h1> heading element
<table>
    <tbody>
        <tr>
            <td colspan="x" rowspan="y">
                {text}
                <p>
                <h1>
                <div>
                <ol>
                    <li>

<ol list-style-type="decimal">
    <li>
        {text}
        <h1>
        <div>
<ul list-style-type="circle">
    <li>
<ol list-style-type="upper-alpha">
    <li>
<ol list-style-type="lower-alpha">
    <li>
<ol list-style-type="upper-roman">
    <li>
<ol list-style-type="lower-roman">
    <li>


Two table elements should not be adjacent - there should be an intervening p element.
It is possible to have an <h1> element as a child of a <td>.
It is possible to have <ol> and <ul> elements as a child of a <td>.
It is possible to have nested <ol> elements - an <ol> is a child of an <ol>

*/
